import { Actions } from "./types";
import { API_BASE } from "../helpers/api";

const CART_TOKEN_KEY = "seff_cart_token";

const getStoredToken = () => {
  try {
    return localStorage.getItem(CART_TOKEN_KEY);
  } catch (err) {
    return null;
  }
};

const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(CART_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(CART_TOKEN_KEY);
    }
  } catch (err) {
    /* ignore storage errors */
  }
};

const handleResponse = async (response) => {
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const detail = data?.detail || response.statusText || "Request failed";
    const error = new Error(detail);
    error.status = response.status;
    throw error;
  }
  return data;
};

const cartLoading = (meta) => ({
  type: Actions.CART_LOADING,
  meta,
});

const cartError = (message, meta) => ({
  type: Actions.CART_ERROR,
  payload: message,
  meta,
});

export const clearCartState = () => ({ type: Actions.CART_CLEAR });

const setCart = (data, meta) => ({
  type: Actions.CART_SET,
  payload: data,
  meta,
});

const checkoutSuccess = (data) => ({
  type: Actions.CART_CHECKOUT_SUCCESS,
  payload: data,
});

export const bootstrapCart = () => async (dispatch) => {
  const token = getStoredToken();
  if (!token) return;
  dispatch(cartLoading({ action: "bootstrap" }));
  try {
    const response = await fetch(`${API_BASE}/store/cart/${token}/`, {
      method: "GET",
      credentials: "include",
    });
    if (response.status === 404) {
      setStoredToken(null);
      dispatch(clearCartState());
      return;
    }
    const data = await handleResponse(response);
    setStoredToken(data?.token || null);
    if (data?.status !== "active") {
      setStoredToken(null);
    }
    dispatch(setCart(data, { action: "bootstrap" }));
  } catch (err) {
    setStoredToken(null);
    dispatch(clearCartState());
  }
};

export const ensureActiveCart = () => async (dispatch, getState) => {
  const { token, status } = getState().cart;
  if (token && status === "active") {
    return token;
  }
  try {
    const response = await fetch(`${API_BASE}/store/cart/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
    const data = await handleResponse(response);
    setStoredToken(data?.token || null);
    dispatch(setCart(data, { action: "create" }));
    return data?.token || null;
  } catch (err) {
    dispatch(cartError(err.message, { action: "create" }));
    throw err;
  }
};

export const addToCart = (productId, quantity = 1) => async (dispatch, getState) => {
  if (!productId) return false;
  dispatch(cartLoading({ action: "add", productId }));
  try {
    let { token, status } = getState().cart;
    if (!token || status !== "active") {
      token = await dispatch(ensureActiveCart());
    }
    const response = await fetch(`${API_BASE}/store/cart/${token}/items/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ product_id: productId, quantity }),
    });
    const data = await handleResponse(response);
    setStoredToken(data?.token || null);
    dispatch(setCart(data, { action: "add", productId }));
    return true;
  } catch (err) {
    dispatch(cartError(err.message, { action: "add", productId }));
    return false;
  }
};

export const addPrintToCart = ({
  printRequestId,
  quantity = 1,
  colorId,
  color,
  material,
  infillPercent,
  notes,
} = {}) => async (dispatch, getState) => {
  if (!printRequestId) return false;
  dispatch(cartLoading({ action: "add_print", printRequestId }));
  try {
    let { token, status } = getState().cart;
    if (!token || status !== "active") {
      token = await dispatch(ensureActiveCart());
    }
    const response = await fetch(`${API_BASE}/store/cart/${token}/add-print/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        print_request_id: printRequestId,
        quantity,
        color_id: colorId,
        color,
        material,
        infill_percent: infillPercent,
        notes,
      }),
    });
    const data = await handleResponse(response);
    setStoredToken(data?.token || null);
    dispatch(setCart(data, { action: "add_print", printRequestId }));
    return true;
  } catch (err) {
    dispatch(cartError(err.message, { action: "add_print", printRequestId }));
    return false;
  }
};

export const addLaserToCart = (laserRequestId) => async (dispatch, getState) => {
  if (!laserRequestId) return false;
  dispatch(cartLoading({ action: "add_laser", laserRequestId }));
  try {
    let { token, status } = getState().cart;
    if (!token || status !== "active") {
      token = await dispatch(ensureActiveCart());
    }
    const response = await fetch(`${API_BASE}/store/cart/${token}/add-laser/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ laser_request_id: laserRequestId }),
    });
    const data = await handleResponse(response);
    setStoredToken(data?.token || null);
    dispatch(setCart(data, { action: "add_laser", laserRequestId }));
    return true;
  } catch (err) {
    dispatch(cartError(err.message, { action: "add_laser", laserRequestId }));
    return false;
  }
};

export const updateCartItemQuantity = (itemId, quantity) => async (dispatch, getState) => {
  const { token } = getState().cart;
  if (!token) return false;
  dispatch(cartLoading({ action: "update", itemId }));
  try {
    const response = await fetch(`${API_BASE}/store/cart/${token}/items/${itemId}/`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ quantity }),
    });
    const data = await handleResponse(response);
    setStoredToken(data?.token || null);
    dispatch(setCart(data, { action: "update", itemId }));
    return true;
  } catch (err) {
    dispatch(cartError(err.message, { action: "update", itemId }));
    return false;
  }
};

export const removeCartItem = (itemId) => async (dispatch, getState) => {
  const { token } = getState().cart;
  if (!token) return false;
  dispatch(cartLoading({ action: "remove", itemId }));
  try {
    const response = await fetch(`${API_BASE}/store/cart/${token}/items/${itemId}/`, {
      method: "DELETE",
      credentials: "include",
    });
    const data = await handleResponse(response);
    setStoredToken(data?.token || null);
    dispatch(setCart(data, { action: "remove", itemId }));
    return true;
  } catch (err) {
    dispatch(cartError(err.message, { action: "remove", itemId }));
    return false;
  }
};

export const checkoutCart = ({
  name,
  email,
  phone,
  notes,
  paymentMethod,
  deliveryMethod,
  shipping,
}) => async (dispatch, getState) => {
  const { token } = getState().cart;
  if (!token) {
    return { success: false, error: "Cart not found." };
  }
  dispatch(cartLoading({ action: "checkout" }));
  try {
    const response = await fetch(`${API_BASE}/store/cart/${token}/checkout/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        email,
        phone,
        notes,
        payment_method: paymentMethod,
        delivery_method: deliveryMethod,
        ...(shipping || {}),
      }),
    });
    const data = await handleResponse(response);
    setStoredToken(null);
    dispatch(checkoutSuccess(data));
    return { success: true, data };
  } catch (err) {
    dispatch(cartError(err.message, { action: "checkout" }));
    return { success: false, error: err.message };
  }
};

export const createPayPalOrder = () => async (dispatch, getState) => {
  const { token, total_cents } = getState().cart;
  if (!token) {
    return { success: false, error: "Cart not found." };
  }
  if (!total_cents || total_cents <= 0) {
    return { success: false, error: "PayPal is only available when the cart has priced items." };
  }
  dispatch(cartLoading({ action: "paypal_create" }));
  try {
    const response = await fetch(`${API_BASE}/store/cart/${token}/paypal/create/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    const data = await handleResponse(response);
    return { success: true, orderId: data.order_id, approvalUrl: data.approval_url };
  } catch (err) {
    dispatch(cartError(err.message, { action: "paypal_create" }));
    return { success: false, error: err.message };
  }
};

export const capturePayPalOrder = (orderId) => async (dispatch, getState) => {
  const { token } = getState().cart;
  if (!token || !orderId) {
    return { success: false, error: "Cart or order not found." };
  }
  dispatch(cartLoading({ action: "paypal_capture" }));
  try {
    const response = await fetch(`${API_BASE}/store/cart/${token}/paypal/capture/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order_id: orderId }),
    });
    const data = await handleResponse(response);
    dispatch(setCart(data, { action: "paypal_capture" }));
    return { success: true, data };
  } catch (err) {
    dispatch(cartError(err.message, { action: "paypal_capture" }));
    return { success: false, error: err.message };
  }
};
